/**
 * Speech-to-text for the Otto composer + reply box, with the same TWO engines
 * as the staff chat hook (`lib/useSpeechToText` — see its header for the full
 * story, ported from the embedded widget's dual-engine `setupDictation`):
 *
 *  1. LIVE (preferred): the browser Web Speech API, single-shot — one phrase,
 *     handed to `onText`. Audio never leaves the machine.
 *  2. RECORDED (fallback): environments whose recognizer has a working mic
 *     but yields nothing (the desktop app's WKWebView — wry implements
 *     WebKit's media-capture permission delegate but not its separate
 *     speech-recognition one — keyless Chromium, Firefox) record the mic via
 *     `lib/dictationFallback` while listening; a session that ends with ZERO
 *     recognized text POSTs the clip to `/api/otto/transcribe` (→ gateway
 *     Whisper) and delivers the transcript through the same `onText`.
 *
 * THE PRIVACY PROPERTY: audio only leaves the machine when the platform
 * recognizer produced nothing. Feature-detected — `supported` is false only
 * where NEITHER engine exists, so callers hide the mic instead of showing a
 * dead button.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

import {
  benchRecognizer,
  canRecordDictation,
  DictationTranscribeError,
  isRecognizerBenched,
  startDictationRecorder,
  transcribeDictationClip,
  type DictationRecorder,
} from '../lib/dictationFallback';

interface SpeechRecognitionLike {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  start(): void;
  stop(): void;
  abort(): void;
  onresult: ((event: { results: ArrayLike<ArrayLike<{ transcript: string }>> }) => void) | null;
  onend: (() => void) | null;
  onerror: ((event: { error?: string }) => void) | null;
}

type SpeechRecognitionCtor = new () => SpeechRecognitionLike;

function getRecognitionCtor(): SpeechRecognitionCtor | null {
  if (typeof window === 'undefined') return null;
  const w = window as unknown as {
    SpeechRecognition?: SpeechRecognitionCtor;
    webkitSpeechRecognition?: SpeechRecognitionCtor;
  };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

interface DictationSession {
  recorder: DictationRecorder | null;
  /** The live engine delivered a phrase. */
  gotText: boolean;
  /** A fatal error said: drop the clip, whatever happens. */
  discard: boolean;
  /** The recognizer was benched mid-session; the recorder carries it now. */
  srDead: boolean;
  /** A LIVE recognizer reported `no-speech`: it works, there was silence. */
  sawNoSpeech: boolean;
}

export interface SpeechToText {
  supported: boolean;
  listening: boolean;
  /** A recorded clip is at the server transcriber right now. */
  transcribing: boolean;
  /** Start listening if idle, stop if already listening. */
  toggle: () => void;
}

export function useSpeechToText(
  onText: (text: string) => void,
  onError?: (code: string) => void,
): SpeechToText {
  const Ctor = getRecognitionCtor();
  const supported = Ctor != null || canRecordDictation();
  const [listening, setListening] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const recRef = useRef<SpeechRecognitionLike | null>(null);
  const sessionRef = useRef<DictationSession | null>(null);
  const disposedRef = useRef(false);
  const onTextRef = useRef(onText);
  const onErrorRef = useRef(onError);
  onTextRef.current = onText;
  onErrorRef.current = onError;

  /** The single way a session ends. Releasing the mic ALWAYS happens;
   * transcribing is conditional on the live engine having produced nothing. */
  const finishSession = useCallback(() => {
    const session = sessionRef.current;
    sessionRef.current = null;
    recRef.current = null;
    setListening(false);
    if (!session?.recorder) return;
    const { recorder } = session;
    if (session.discard || session.gotText || (session.sawNoSpeech && !session.srDead)) {
      recorder.discard(); // the live engine won, or affirmatively heard silence
      return;
    }
    void (async () => {
      const blob = await recorder.finish();
      if (!blob || disposedRef.current) return;
      setTranscribing(true);
      try {
        const text = await transcribeDictationClip(blob);
        if (text && !disposedRef.current) onTextRef.current(text);
      } catch (err) {
        if (!disposedRef.current) {
          onErrorRef.current?.(
            err instanceof DictationTranscribeError ? err.code : 'transcribe-failed',
          );
        }
      } finally {
        setTranscribing(false);
      }
    })();
  }, []);

  useEffect(() => {
    disposedRef.current = false;
    return () => {
      // Never leave the mic hot, never post a clip nobody is waiting for.
      disposedRef.current = true;
      try {
        recRef.current?.abort();
      } catch {
        /* ignore */
      }
      recRef.current = null;
      sessionRef.current?.recorder?.discard();
      sessionRef.current = null;
    };
  }, []);

  const toggle = useCallback(() => {
    // ── Stop ──
    if (sessionRef.current || recRef.current) {
      const session = sessionRef.current;
      const rec = recRef.current;
      if (rec && !session?.srDead) {
        try {
          rec.stop(); // → onend → finishSession
          return;
        } catch {
          /* fall through to a direct finish */
        }
      }
      if (rec) {
        try {
          rec.abort(); // a benched engine can't be trusted to fire onend
        } catch {
          /* ignore */
        }
        recRef.current = null;
      }
      finishSession();
      return;
    }

    // ── Start ──
    const useRecognizer = Ctor != null && !isRecognizerBenched();
    const recorder = startDictationRecorder(); // null where recording is unsupported
    if (!useRecognizer && !recorder) return; // no engine at all
    const session: DictationSession = {
      recorder,
      gotText: false,
      discard: false,
      srDead: false,
      sawNoSpeech: false,
    };
    sessionRef.current = session;

    if (!useRecognizer) {
      // Recorded engine only: capture now, transcribe on the stopping toggle.
      setListening(true);
      return;
    }

    const rec = new (Ctor as SpeechRecognitionCtor)();
    rec.continuous = false;
    rec.interimResults = false;
    rec.lang = (typeof navigator !== 'undefined' && navigator.language) || 'en-US';
    rec.onresult = (event) => {
      let text = '';
      for (let i = 0; i < event.results.length; i += 1) {
        text += `${event.results[i]?.[0]?.transcript ?? ''} `;
      }
      const trimmed = text.trim();
      if (trimmed) {
        session.gotText = true;
        session.sawNoSpeech = false;
        onTextRef.current(trimmed);
      }
    };
    rec.onend = () => {
      if (session.srDead && sessionRef.current === session) {
        // The benched recognizer wound down; the recorder carries the
        // session until the user taps the mic again.
        recRef.current = null;
        return;
      }
      recRef.current = null;
      finishSession();
    };
    rec.onerror = (event) => {
      const code = event?.error ?? '';
      if (code === 'no-speech') {
        session.sawNoSpeech = true;
        return; // benign — onend decides
      }
      if (code === 'aborted') return;
      if (session.recorder && code !== 'audio-capture') {
        // The RECOGNIZER failed, not the mic (WKWebView's speech-permission
        // `not-allowed`, keyless Chromium's `network`, …) — bench it for the
        // page and carry the session on the recorder. No dead-end for the user.
        benchRecognizer();
        session.srDead = true;
        return;
      }
      // A real mic failure: nothing to rescue — drop the clip and end the
      // session now (a broken engine can't be trusted to fire onend),
      // surfacing the code where a caller listens.
      session.discard = true;
      onErrorRef.current?.(code || 'audio-capture');
      if (sessionRef.current === session) {
        try {
          rec.abort();
        } catch {
          /* ignore */
        }
        finishSession();
      }
    };
    recRef.current = rec;
    try {
      rec.start();
      setListening(true);
    } catch {
      recRef.current = null;
      if (session.recorder) {
        // The recognizer refused to even start — record instead.
        benchRecognizer();
        session.srDead = true;
        setListening(true);
        return;
      }
      sessionRef.current = null;
      setListening(false);
    }
  }, [Ctor, finishSession]);

  return { supported, listening, transcribing, toggle };
}
